import {
  Attributes,
  AttributesRow,
  ButtonProps,
  Card,
  DataStatus,
  showNotification,
} from 'platform/components';
import {Box, Heading, HStack, Link, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {equals, isNil, mergeAll, not} from 'ramda';
import {isArray, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {
  FastTrackBasketItem,
  GetFastTrackIssueResponse,
  PostFastTrackIssueBasketAddRequest,
  useAddFastTrackBasketItemMutation,
  useChangeFastTrackBasketItemQuantityMutation,
  useCheckoutFastTrackBasketMutation,
  useDeleteFastTrackBasketItemMutation,
  useDiscardFastTrackBasketItemMutation,
  useGetDirectSaleQuery,
  useGetFastTrackBasketQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {customerRoutes, vehiclesRoutes, warehouseRoutes, workshopRoutes} from '@dms/routes';
import {
  AfterSalesMaterialBasket,
  DmsDataGrid,
  EitherQuantityOrError,
  FullScreenModal,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  getCustomerName,
  handleApiError,
  logError,
  queryParams,
  refreshDmsDatagrid,
  useBasketItemsQuantityState,
} from '@dms/shared';

import {composePath, EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['originEntityId', 'originEntityType'] as const;

type CastedRowData = {
  originEntityId: {
    value: string;
  };
  originEntityType: {
    value: string;
  };
};

interface AddMaterialModalProps extends TestIdProps {
  fastTrackId: string;
  fastTrack: GetFastTrackIssueResponse;
  onDiscard: () => void;
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function AddMaterialModal(props: AddMaterialModalProps) {
  const directSale = props.fastTrack.originEntityHeader.directSale;
  const serviceOrder = props.fastTrack.originEntityHeader.serviceOrder;

  const isOriginEntityHeaderDirectSale = isNotNilOrEmpty(directSale);

  const directSaleId = directSale?.directSaleId;
  const vehicleId = isOriginEntityHeaderDirectSale
    ? directSale?.vehicleId
    : serviceOrder?.vehicleId;
  const customerId = isOriginEntityHeaderDirectSale
    ? directSale?.customerId
    : serviceOrder?.customerId;

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: isBasketError,
  } = useGetFastTrackBasketQuery({fastTrackId: props.fastTrackId});

  const {
    data: directSaleDetail,
    isLoading: isDirectSaleDetailLoading,
    isError: isDirectSaleDetailError,
  } = useGetDirectSaleQuery({directSaleId: directSaleId as string}, {skip: !directSaleId});

  const {
    data: vehicleDetail,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery({vehicleId: vehicleId as string}, {skip: isNilOrEmpty(vehicleId)});

  const {
    data: customerDetail,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useGetCustomerV2Query(
    {customerId: customerId as string},
    {
      skip: isNilOrEmpty(customerId),
    }
  );

  const [deleteFastTrackBasketItem, {isLoading: isDeletingItem}] =
    useDeleteFastTrackBasketItemMutation();

  const [changeFastTrackBasketItemQuantity, {isLoading: isChangingQuantity}] =
    useChangeFastTrackBasketItemQuantityMutation();

  const [checkoutFastTrackBasket, {isLoading: isCheckingOutBasket}] =
    useCheckoutFastTrackBasketMutation();

  const [addFastTrackBasketItem, {isLoading: isAddingItem}] = useAddFastTrackBasketItemMutation();

  const [discardFastTrackBasket, {isLoading: isDiscardingBasket}] =
    useDiscardFastTrackBasketItemMutation();

  const isLoading =
    isBasketLoading || isDirectSaleDetailLoading || isVehicleLoading || isCustomerLoading;

  const isError = isBasketError || isDirectSaleDetailError || isVehicleError || isCustomerError;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItem ||
    isDeletingItem ||
    isChangingQuantity ||
    isDiscardingBasket ||
    isCheckingOutBasket;

  const basketItems = basket?.fastTrackBasketItem ?? [];
  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketItemsWithZeroCorrectionValue = basketItems
    .filter((basketItem) => basketItem.quantity === 0)
    .map((basketItem) => basketItem.id);

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const hasInvalidItems =
    hasInvalidBasketItems || isNotNilOrEmpty(basketItemsWithZeroCorrectionValue);

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {fastTrackId: props.fastTrackId}]),
    [props.fastTrackId]
  );

  const handleDiscard = async () => {
    await discardFastTrackBasket({fastTrackId: props.fastTrackId})
      .unwrap()
      .then(props.onDiscard)
      .catch(handleApiError);
  };

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await changeFastTrackBasketItemQuantity({
      fastTrackId: props.fastTrackId,
      fastTrackItemId: itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleDelete = async (itemId: string[]) => {
    await deleteFastTrackBasketItem({
      fastTrackId: props.fastTrackId,
      body: {
        fastTrackItemIds: itemId,
      },
    })
      .unwrap()
      .then(() => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.fastTrackIssueAddItems))
      .catch(handleApiError);
  };

  const handleCheckout = async () => {
    await checkoutFastTrackBasket({fastTrackId: props.fastTrackId})
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.materialAdded')))
      .then(props.onDiscard)
      .then(() => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.fastTrackIssueItems))
      .catch(handleApiError);
  };

  const actions: ButtonProps[] = [
    {
      isLoading: isDiscardingBasket,
      isDisabled: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
      'data-testid': suffixTestId('headerActions.discard', props),
    },
    {
      isLoading: isCheckingOutBasket,
      isDisabled: isBasketEmpty || hasPendingRequest || hasUnsettledQuantity,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowData,
    refreshData,
  }) => {
    if (hasPendingRequest) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    match(actionKey)
      .with('add', () => {
        const castedRowData = (isArray(rowData) ? rowData : [rowData]) as CastedRowData[];

        const itemsPayload: PostFastTrackIssueBasketAddRequest['body']['items'] = [];

        castedRowData.forEach((row) => {
          const originEntityId = row?.originEntityId?.value;
          const originEntityType = row?.originEntityType?.value;

          if (isNil(originEntityId)) {
            return logError('originEntityId is not defined');
          }

          if (isNil(originEntityType)) {
            return logError('originEntityType is not defined');
          }

          itemsPayload.push({
            originEntityItemId: row.originEntityId.value,
            originEntityItemType: row.originEntityType.value,
          });
        });

        addFastTrackBasketItem({
          fastTrackId: props.fastTrackId,
          body: {
            items: itemsPayload,
          },
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const title = isOriginEntityHeaderDirectSale
    ? i18n.t('entity.warehouse.labels.directSale')
    : i18n.t('entity.warehouse.labels.serviceOrder');

  const directSaleInformation: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: directSaleDetail?.number ? (
        <Link
          title={directSaleDetail?.number}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(warehouseRoutes.directSalesDetailOverview, {
            params: {id: directSale?.directSaleId},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.orderType'),
      value: directSale?.type.name,
    },
    {
      label: i18n.t('general.labels.variant'),
      value: directSale?.variant.name,
    },
    {
      label: i18n.t('general.labels.customer'),
      content: customerDetail ? (
        <Link
          title={getCustomerName(customerDetail)}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(customerRoutes.detail, {
            params: {id: customerDetail?.id},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      content: vehicleDetail ? (
        <Link
          title={vehicleDetail?.title}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(vehiclesRoutes.detail, {
            params: {id: vehicleDetail?.id},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.licensePlate'),
      value: vehicleDetail?.state?.registrationPlate,
    },
    {
      label: i18n.t('entity.warehouse.labels.vin'),
      value: vehicleDetail?.vin,
    },
  ];

  const serviceOrderInformation: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: serviceOrder?.number ? (
        <Link
          title={serviceOrder?.number}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: serviceOrder?.serviceCaseId},
            queryParams: {
              [queryParams.SERVICE_CASE_ORDER_ID]: serviceOrder?.serviceOrderId,
            },
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.orderType'),
      value: serviceOrder?.type.name,
    },
    {
      label: i18n.t('general.labels.variant'),
      value: serviceOrder?.variant.name,
    },
    {
      label: i18n.t('general.labels.customer'),
      content: customerDetail ? (
        <Link
          title={getCustomerName(customerDetail)}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(customerRoutes.detail, {
            params: {id: customerDetail?.id},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      content: vehicleDetail ? (
        <Link
          title={vehicleDetail?.title}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(vehiclesRoutes.detail, {
            params: {id: vehicleDetail?.id},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.licensePlate'),
      value: vehicleDetail?.state?.registrationPlate,
    },
    {
      label: i18n.t('entity.warehouse.labels.vin'),
      value: vehicleDetail?.vin,
    },
  ];

  return (
    <FullScreenModal headline={i18n.t('general.actions.addMaterial')} actions={actions}>
      <DataStatus isLoading={isLoading} isError={isError}>
        <Box padding={4} height="100%">
          <HStack spacing={4} height="100%">
            <Box height="100%" flex={5}>
              <Card isFullHeight title={title}>
                <VStack height="100%" spacing={4}>
                  <Show when={directSale}>
                    <Attributes size="quarter" rows={directSaleInformation} />
                  </Show>
                  <Show when={serviceOrder}>
                    <Attributes size="quarter" rows={serviceOrderInformation} />
                  </Show>
                  <Heading size={4}>{i18n.t('entity.warehouse.labels.orderItems')}</Heading>
                  <DmsDataGrid
                    gridCode={WAREHOUSE_GRID_CODES.fastTrackIssueAddItems}
                    queryModifier={queryModifier}
                    actionCallback={actionCallback}
                    predefinedColumns={PREDEFINED_COLUMNS}
                    data-testid={WAREHOUSE_GRID_CODES.fastTrackIssueAddItems}
                  />
                </VStack>
              </Card>
            </Box>
            <Box flex={1} minWidth={86}>
              <AfterSalesMaterialBasket<FastTrackBasketItem>
                basket={{
                  items: basketItems,
                  totalPrice: null,
                  isLoading: isBasketLoading,
                  hasError: isBasketError,
                  hasPendingRequest,
                  hasInvalidItems,
                  isPriceHidden: true,
                  isEditItemHidden: true,
                }}
                mechanic={{
                  isMechanicHidden: true,
                }}
                onQuantityChange={handleQuantityChange}
                onEdit={() => {}}
                onDelete={handleDelete}
                data-testid={suffixTestId('basket', props)}
              />
            </Box>
          </HStack>
        </Box>
      </DataStatus>
    </FullScreenModal>
  );
}
